import React, { useRef, useCallback } from 'react';
import { ChevronRight, ChevronLeft, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../hooks/useSettings';
import FeaturedProductCard from '../common/FeaturedProductCard.jsx';

export default function FeaturedProductsSection({ products, isLoading, title }) {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const { branding } = useSettings();
  const primaryColor = branding?.themeColor || '#f97316';
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  if (!isLoading && (!products || products.length === 0)) return null;

  const scrollBy = (dir) => scrollRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });

  const onMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = 'grabbing';
  };
  const onMouseLeave = () => { isDragging.current = false; if (scrollRef.current) scrollRef.current.style.cursor = 'grab'; };
  const onMouseUp = () => { isDragging.current = false; if (scrollRef.current) scrollRef.current.style.cursor = 'grab'; };
  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    scrollRef.current.scrollLeft = scrollLeft.current - (x - startX.current) * 1.5;
  };

  return (
    <section className="w-full px-2 sm:px-4">
      <div className="rounded-[1.75rem] sm:rounded-[2.5rem] p-1 sm:p-2">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-5 sm:mb-6">
          <h2 className="text-lg sm:text-2xl font-black tracking-tight text-slate-900">
            {title || 'Featured Export Products'}
          </h2>
          <button
            onClick={() => navigate('/products?featured=yes')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] sm:text-xs font-bold hover:opacity-80 transition-opacity whitespace-nowrap active:scale-95 shrink-0"
            style={{ color: primaryColor, backgroundColor: `${primaryColor}1a` }}
          >
            View all products
            <ArrowRight size={14} />
          </button>
        </div>

        {/* Carousel with side arrows */}
        <div className="relative">
          <button
            onClick={() => scrollBy(-1)}
            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 w-10 h-10 bg-white rounded-full items-center justify-center text-slate-700 border border-slate-200 shadow-md hover:border-slate-900 hover:text-slate-900 transition-all"
            aria-label="Scroll left"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => scrollBy(1)}
            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-10 h-10 bg-white rounded-full items-center justify-center text-slate-700 border border-slate-200 shadow-md hover:border-slate-900 hover:text-slate-900 transition-all"
            aria-label="Scroll right"
          >
            <ChevronRight size={18} />
          </button>

          <div ref={scrollRef} className="flex gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar scroll-smooth pb-1" style={{cursor:'grab'}} onMouseDown={onMouseDown} onMouseLeave={onMouseLeave} onMouseUp={onMouseUp} onMouseMove={onMouseMove}>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={`skeleton-${i}`} className="w-[185px] h-[330px] shrink-0 rounded-2xl bg-slate-100 animate-pulse" />
              ))
            ) : (
              products.map((product, i) => (
                <div key={product.id || i} style={{ width: '185px', minWidth: '185px', maxWidth: '185px', flexShrink: 0, flexGrow: 0, height: '365px' }}>
                  <FeaturedProductCard product={product} index={i} imageHeight="176px" />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
